import React from 'react';
import { Link } from 'react-router-dom';
import QuoteWidget from '@/components/QuoteWidget';
import { Shield, ArrowRight, CheckCircle2 } from 'lucide-react';

const ServicesPage = () => {
  const services = [
    {
      title: "Term Life Insurance",
      path: "/services/term-life",
      description: "Affordable protection for a specific period with high coverage amounts at low premiums.",
      features: ["High coverage at low cost", "Fixed premium payments", "Flexible terms (10-30 years)"]
    },
    {
      title: "Whole Life Insurance",
      path: "/services/whole-life",
      description: "Permanent protection with guaranteed cash value accumulation and lifetime coverage.",
      features: ["Lifetime protection", "Guaranteed death benefit", "Cash value growth"]
    },
    {
      title: "Universal Life Insurance",
      path: "/services/universal-life",
      description: "Flexible permanent life insurance with adjustable premiums and benefits.",
      features: ["Flexible premium structure", "Adjustable death benefit", "Cash value potential"]
    },
    {
      title: "Premium Financing",
      path: "/premium-financing",
      description: "Leverage capital to secure large coverage amounts while preserving liquidity.",
      features: ["Preserve investment capital", "Access larger coverage", "Estate planning optimization"]
    },
    {
      title: "Estate Planning Guidance",
      path: "/services/estate-planning",
      description: "Strategies to protect and transfer wealth efficiently across generations.",
      features: ["Life insurance trusts", "Tax-efficient transfers", "Legacy planning"]
    }
  ];

  return (
    <div className="bg-[#020617] min-h-screen pt-20" data-testid="services-page">
      {/* Hero */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-[#C5A059] text-sm uppercase tracking-widest mb-4 block">
                Our Services
              </span>
              <h1 className="font-serif text-4xl md:text-5xl text-white leading-tight mb-6">
                Financial Protection & Wealth Planning
              </h1>
              <p className="text-slate-300 text-lg mb-8">
                ShopCompareInsure provides life insurance and financial planning strategies designed to help individuals, families, and business owners protect their financial future.
              </p>
              <p className="text-slate-400">
                Our advisors work with multiple insurance carriers to provide solutions aligned with each client's goals.
              </p>
            </div>
            <div className="hidden lg:block">
              <QuoteWidget />
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24 bg-[#0F172A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="font-serif text-3xl text-white mb-4">Our Services</h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Comprehensive financial protection solutions for every stage of life
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <Link
                key={index}
                to={service.path}
                className="group bg-slate-900/50 border border-slate-800 p-8 hover:border-[#C5A059]/30 transition-all"
              >
                <h3 className="font-serif text-xl text-white mb-3 group-hover:text-[#C5A059] transition-colors">
                  {service.title}
                </h3>
                <p className="text-slate-400 text-sm mb-6">{service.description}</p>
                <ul className="space-y-2 mb-6">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center space-x-2 text-sm text-slate-500">
                      <CheckCircle2 className="w-4 h-4 text-[#C5A059]" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <span className="inline-flex items-center text-[#C5A059] text-sm font-semibold uppercase tracking-wider group-hover:translate-x-1 transition-transform">
                  Learn More <ArrowRight className="w-4 h-4 ml-2" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-serif text-3xl text-white mb-6">Ready to Get Started?</h2>
          <p className="text-slate-400 mb-8 max-w-2xl mx-auto">
            Compare quotes from top-rated carriers or speak with an advisor to find the right solution.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/compare"
              className="inline-flex items-center justify-center bg-[#C5A059] text-[#020617] hover:bg-[#D4AF37] font-semibold px-8 py-4 uppercase tracking-wider text-sm transition-all"
            >
              Compare Quotes
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center border border-slate-600 text-slate-300 hover:border-[#C5A059] hover:text-[#C5A059] px-8 py-4 uppercase tracking-wider text-sm transition-all"
            >
              Speak with an Advisor
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
